import { useEffect, useState } from 'react';
import { useAuth0Safe as useAuth0 } from '@/hooks/useAuth0Safe';
import { Bookmark } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getLocalToken() {
  try { return localStorage.getItem('emina_local_admin_token'); } catch { return null; }
}

export default function BookmarkButton({ articleId, variant = 'icon' }) {
  const { isAuthenticated, getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const [active, setActive] = useState(false);
  const [loading, setLoading] = useState(false);

  async function auth() {
    const local = getLocalToken();
    if (local) return local;
    if (isAuthenticated) {
      try { return await getAccessTokenSilently(); } catch { return null; }
    }
    return null;
  }

  useEffect(() => {
    let alive = true;
    (async () => {
      const t = await auth();
      if (!t || !articleId) return;
      try {
        const r = await fetch(`${API}/public/bookmarks/check/${articleId}`,
          { headers: { Authorization: `Bearer ${t}` } });
        if (r.ok) {
          const d = await r.json();
          if (alive) setActive(!!d.bookmarked);
        }
      } catch {}
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [articleId, isAuthenticated]);

  async function toggle(e) {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    const t = await auth();
    if (!t) {
      toast.message('Prijavite se za spremanje članaka');
      setTimeout(() => { try { loginWithRedirect(); } catch {} }, 400);
      return;
    }
    setLoading(true);
    try {
      if (active) {
        await fetch(`${API}/public/bookmarks/${articleId}`,
          { method: 'DELETE', headers: { Authorization: `Bearer ${t}` } });
        setActive(false);
        toast.success('Uklonjeno iz spremljenih');
      } else {
        await fetch(`${API}/public/bookmarks`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${t}` },
          body: JSON.stringify({ article_id: articleId }),
        });
        setActive(true);
        toast.success('Spremljeno');
      }
    } catch {
      toast.error('Greška pri spremanju');
    } finally { setLoading(false); }
  }

  if (variant === 'full') {
    return (
      <button onClick={toggle} disabled={loading}
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${active
          ? 'bg-amber-500 text-white hover:bg-amber-600'
          : 'bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200'}`}
        data-testid="bookmark-btn">
        <Bookmark size={13} fill={active ? 'currentColor' : 'none'} />
        {active ? 'Spremljeno' : 'Spremi'}
      </button>
    );
  }

  return (
    <button onClick={toggle} disabled={loading}
      aria-label={active ? 'Ukloni iz spremljenih' : 'Spremi članak'}
      title={active ? 'Ukloni iz spremljenih' : 'Spremi članak'}
      className={`p-2 rounded-lg transition-colors ${active
        ? 'bg-amber-500 text-white hover:bg-amber-600'
        : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-amber-500 hover:text-white'}`}
      data-testid="bookmark-btn">
      <Bookmark size={14} fill={active ? 'currentColor' : 'none'} />
    </button>
  );
}
